<template>
  <section class="period-metric">
    <h2 class="title">{{ label }}</h2>
    <div class="legend">
      <div class="average">
        <span>{{ sub }}</span>
        <strong><span v-if="approx" class="approx-mark">~</span>{{ value == null ? '—' : fmt(value) }} <small v-if="unit">{{ unit }}</small></strong>
      </div>
      <div v-if="normLines.length || normText || sub !== 'в среднем'" class="norms">
        <template v-if="normLines.length">
          <div v-for="(line, i) in normLines" :key="i" class="norm-row">
            <svg :style="{ color: line.color || normColor }" width="25" height="12" viewBox="0 0 25 12" aria-hidden="true"><path d="M0 6h25" stroke="currentColor" stroke-width="1" :stroke-dasharray="line.dashed ? '3 3' : undefined" /></svg>
            <span class="norm-copy"><span class="norm-phrase">{{ line.text }}</span><template v-if="line.detail">{{ ' ' }}<span class="norm-phrase">{{ line.detail }}</span></template></span>
          </div>
        </template>
        <div v-else class="norm-row">
          <svg v-if="normBand" class="norm-band" width="25" height="18" viewBox="0 0 25 18" aria-hidden="true">
            <rect x="0" y="3" width="25" height="12" fill="var(--ink)" fill-opacity=".05" />
            <path d="M0 3h25M0 15h25" stroke="var(--ink)" stroke-opacity=".3" stroke-width="1" stroke-dasharray="3 3" />
          </svg>
          <svg v-else-if="/^(норма|(?:ограничение )?до) /.test(normText)" :style="{ color: normColor }" width="25" height="12" viewBox="0 0 25 12" aria-hidden="true"><path d="M0 6h25" stroke="currentColor" stroke-width="1" /></svg>
          <span>{{ normText || sub }}</span>
        </div>
      </div>
    </div>
    <slot />
    <button v-if="$slots.more" class="more" @click="$emit('update:open', !open)" :aria-expanded="open">
      <span class="btn-pill">
        <span>{{ moreLabel }}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" :style="{ transform: open ? 'rotate(180deg)' : '' }"><path d="M6 9l6 6 6-6" /></svg>
      </span>
    </button>
    <ExpandTransition><div v-if="open && $slots.more" class="more-body"><slot name="more" /></div></ExpandTransition>
  </section>
</template>

<script setup>
import ExpandTransition from './ExpandTransition.vue'
const props = defineProps({
  label: { type: String, required: true },
  sub: { type: String, default: 'в среднем' },
  value: { type: Number, default: null },
  unit: { type: String, default: 'г' },
  unitLine: { type: String, default: '' },
  normText: { type: String, default: '' },
  normColor: { type: String, default: 'var(--ink-70)' },
  normBand: Boolean,
  normLines: { type: Array, default: () => [] },
  decimals: { type: Number, default: 0 },
  approx: { type: Boolean, default: false },
  moreLabel: { type: String, default: 'Основные источники' },
  open: { type: Boolean, default: false },
})
defineEmits(['update:open'])
function fmt(v) {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: props.decimals }).format(v)
}
</script>

<style scoped>
.period-metric { margin: 0 0 30px; font-family: Inter, sans-serif; color: var(--ink); }
.title { margin: 0 0 12px; font-size: var(--fs-20); line-height: 1.2; font-weight: 700; }
.legend { display: flex; align-items: stretch; gap: 6px; margin-bottom: 6px; }
.average, .norms { background: #FFFBF4; border: 1px solid var(--ink-10); border-radius: 12px; padding: 10px 12px; }
.average { display: flex; flex-direction: column; justify-content: flex-start; gap: 3px; min-width: 0; flex: 0 0 43%; }
.average strong { font-size: var(--fs-22); font-weight: 700; line-height: 1.15; font-variant-numeric: tabular-nums; white-space: nowrap; }
.average small { font-size: var(--fs-13); font-weight: 500; }
.average > span { font-size: var(--fs-13); line-height: 1.4; color: var(--ink-70); }
.norms { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 5px; }
.norm-row { display: flex; align-items: flex-start; gap: 8px; font-size: var(--fs-13); line-height: 1.4; }
.norm-row svg { flex: none; margin-top: calc((var(--fs-13) * 1.4 - 12px) / 2); color: var(--ink-70); }
.norm-row .norm-band { margin-top: calc((var(--fs-13) * 1.4 - 18px) / 2); }
.norm-copy { min-width: 0; }
.norm-phrase { white-space: nowrap; }
.more { width: 100%; min-height: 44px; display: flex; justify-content: flex-end; align-items: center; margin-top: 4px; }
.more:active .btn-pill { opacity: .6; }
.more svg { transition: transform .2s; }
.more-body { margin-top: 2px; }
@media (prefers-reduced-motion: reduce) { .more svg { transition: none; } }
</style>
